import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';

// devextreme imports
import { DxDataGridComponent, DxPopupComponent } from 'devextreme-angular';
import notify from 'devextreme/ui/notify';
import CheckBox from 'devextreme/ui/check_box';
import * as events from 'devextreme/events';

// cc-common imports
import { IconConstants, Utils } from 'cc-common';

// service imports
import { WorkflowSharedService } from '../../../../shared-workflow-module/_services/workflow-shared.service';
import { WorkflowReimbursementRequestService } from '../../../_services/workflow-reimbursement-request.service';
import { ServerErrorService } from '../../../../_services/shared-services/server-error.service';
import { UserService } from '../../../../_services/data-services/user.service';
import { HelperService } from '../../../../_services/shared-services/helper.service';
import { PrecisionService } from '../../../../_services/shared-services/precision.service';

// model imports
import { WFReimbursementRequestRawDto } from '../../../_models/workflow-reimbursement-request/wf-reimbursement-request-raw-dto';
import { WFReimbursementRequestLineItemsResultDto } from '../../../_models/workflow-reimbursement-request/wf-reimbursement-request-line-items-result-dto';
import { WFReimbursementRequestEditorResultDto } from '../../../_models/workflow-reimbursement-request/wf-reimbursement-request-editor-result-dto';
import { WFToggleReimbursableParametersDto } from '../../../_models/workflow-reimbursement-request/wf-toggle-reimbursable-parameters-dto';
import { WFReimbursementRequestEditorParameterDto } from '../../../_models/workflow-reimbursement-request/wf-reimbursement-request-editor-parameter-dto';
import { WorkFlowParametersDto } from '../../../../shared-workflow-module/_models/workflow-parameters-dto';
import { WFEBankAccountResultDto } from '../../../../shared-workflow-module/_models/wfe-bank-account-result-dto';
import { ActionMenuEvent, ActionMenuInfo } from '../../../../shared-module/_models/action-menu-info';
import { APBillParameterDto } from '../../../../cirabooks/shared-cirabooks-module/_models/accounts-payable/ap-bill/ap-bill-parameter-dto';

// constant imports
import { MessageInterval } from '../../../../_constants/messages-intervals';
import { ActionConstants } from '../../../../_constants/action-constants';

@Component({
    selector: 'app-workflow-reimbursement-editor',
    templateUrl: './workflow-reimbursement-editor.component.html',
    styleUrls: ['./workflow-reimbursement-editor.component.scss']
})
export class WorkflowReimbursementEditorComponent implements OnInit {
    @ViewChild('reimbursementEditorPopup', { static: false }) reimbursementEditorPopup: DxPopupComponent;
    @ViewChild('reimbursementEditorGrid', { static: false }) reimbursementEditorGrid: DxDataGridComponent;

    @Input() workflowId: number;
    @Input() communityId: number;
    @Input() title: string;
    @Input() managementCompanyId: number;

    @Output() onClose = new EventEmitter<boolean>();

    visible = false;

    lineitemFilterOptions: any[] = [];
    showReimbursableChargesOnly: boolean;

    sourceBankAccountBoxValue: number[] = [];
    targetBankAccountBoxValue: number[] = [];
    bankAccountColumns: any[];

    reimbursementEditor: WFReimbursementRequestEditorResultDto;
    reimbursementRawDto: WFReimbursementRequestRawDto;
    lineItemsSource: WFReimbursementRequestLineItemsResultDto[] = [];
    selectedReimbursementLineItem: WFReimbursementRequestLineItemsResultDto;
    selectedReimbursementKeys: number[] = [];

    actionMenuItems: Array<ActionMenuInfo> = [];
    currencyFormat: string;

    iconCancel = IconConstants.CANCEL;
    iconSave = IconConstants.SAVE;
    iconPDF = IconConstants.PDF_FILE_GRID;

    showAttachmentReceipt = false;
    showReclassify = false;
    apBillDetailParam: APBillParameterDto;

    toolbarButtonPosition = 'before';

    isInternalUser = false;
    loadingVisible = false;

    constructor(
        private readonly userService: UserService,
        private readonly workflowReimbursementService: WorkflowReimbursementRequestService,
        private readonly helperService: HelperService,
        private readonly workflowSharedService: WorkflowSharedService,
        private readonly errorService: ServerErrorService,
        private readonly precisionService: PrecisionService
    ) {
        this.currencyFormat = this.precisionService.getCurrencyFormat();
        this.customizeLineItemSummary = this.customizeLineItemSummary.bind(this);
    }

    ngOnInit() {
        this.isInternalUser = this.userService.isInternal();

        this.reimbursementEditor = new WFReimbursementRequestEditorResultDto();
        this.reimbursementRawDto = new WFReimbursementRequestRawDto();

        this.show();
        this.getDefaultConfigurations();
        this.getReimbursementEditor();
        this.actionMenuItems = [
            {
                action: ActionConstants.ATTACH_RECIEPT,
                name: 'Attach Receipt'
            },
            {
                action: ActionConstants.RECLASSIFY,
                name: 'Reclassify'
            },
            {
                action: ActionConstants.NON_REIMBURSABLE,
                name: 'Set Charge As Non Reimbursable'
            },
            {
                action: ActionConstants.REIMBURSABLE,
                name: 'Set Charge As Reimbursable'
            }
        ];
    }

    show(): void {
        this.visible = true;
    }

    onPopupShown(): void {
        this.reimbursementEditorPopup.instance.repaint();
    }

    onPopupClose(): void {
        this.visible = false;
        this.closeEditor();
    }

    onCancelClick(): void {
        this.closeEditor();
    }

    onSaveClick(): void {
        this.saveReimbursementEditor();
    }

    isActionVisible(item: ActionMenuInfo, data: WFReimbursementRequestLineItemsResultDto): boolean {
        let isActionItemVisible = false;
        if (
            (Utils.equalsIgnoreCase(item.action, ActionConstants.ATTACH_RECIEPT) &&
                Utils.isTrue(data.allowAttachReceipt)) ||
            (Utils.equalsIgnoreCase(item.action, ActionConstants.NON_REIMBURSABLE) &&
                Utils.isTrue(data.allowToggleReimbursable) &&
                Utils.isTrue(data.isReimbursable)) ||
            (Utils.equalsIgnoreCase(item.action, ActionConstants.REIMBURSABLE) &&
                Utils.isTrue(data.allowToggleReimbursable) &&
                Utils.isFalse(data.isReimbursable)) ||
            (Utils.equalsIgnoreCase(item.action, ActionConstants.RECLASSIFY) && Utils.isTrue(data.allowReclassify))
        ) {
            isActionItemVisible = true;
        }
        return isActionItemVisible;
    }

    onActionClick(event: ActionMenuEvent<WFReimbursementRequestLineItemsResultDto>): void {
        this.selectedReimbursementLineItem = event.record;
        if (Utils.equalsIgnoreCase(event.action, ActionConstants.ATTACH_RECIEPT)) {
            this.showAttachmentReceipt = true;
        } else if (Utils.equalsIgnoreCase(event.action, ActionConstants.NON_REIMBURSABLE)) {
            this.clearLineItemSelection();
            this.toggleReimbursement();
        } else if (Utils.equalsIgnoreCase(event.action, ActionConstants.REIMBURSABLE)) {
            this.toggleReimbursement();
        } else if (Utils.equalsIgnoreCase(event.action, ActionConstants.RECLASSIFY)) {
            this.reclassifyWorkflow();
        }
    }

    onSourceAccountSelection(selectedObject: any): void {
        if (Utils.notNullAndDefined(selectedObject) && Utils.notNullOrEmpty(selectedObject.selectedRow)) {
            const selectedAccount = selectedObject.selectedRow[0];
            this.reimbursementRawDto.sourceBankAccountId = selectedAccount.bankAccountId;
        }
    }

    onTargetAccountSelection(selectedObject: any): void {
        if (Utils.notNullAndDefined(selectedObject) && Utils.notNullOrEmpty(selectedObject.selectedRow)) {
            const selectedAccount = selectedObject.selectedRow[0];
            this.reimbursementRawDto.targetBankAccountId = selectedAccount.bankAccountId;
        }
    }

    customizeLineItemSummary(e: any): string {
        return `Total: ${this.helperService.getCurrencyStringFormat(e.value)}`;
    }

    onLineItemsFilterOptionChange(): void {
        this.prepareLineItems();
    }

    onCellPrepared(e: any): void {
        if (Utils.equalsIgnoreCase(e.rowType, 'data') && Utils.equalsIgnoreCase(e.column.command, 'select')) {
            const checkboxElement = e.cellElement.querySelector('.dx-select-checkbox');
            const instance = CheckBox.getInstance(checkboxElement);
            if (Utils.isFalse(e.data.allowSelection)) {
                instance.option('disabled', true);
                events.off(e.cellElement);
            } else if (Utils.isFalse(e.data.isReimbursable)) {
                instance.option('visible', false);
                events.off(e.cellElement);
            }
        }
    }

    onGridSelectionChange(event: any): void {
        setTimeout(() => {
            const selectedRowsData = event.selectedRowsData;
            if (Utils.notNullOrEmpty(selectedRowsData)) {
                let totalSummary = 0;
                selectedRowsData.forEach((row) => {
                    totalSummary += row.amount;
                });
                this.reimbursementRawDto.amount = this.precisionService.getRoundedNumber(totalSummary);
            } else {
                this.reimbursementRawDto.amount = 0;
            }

            this.reimbursementRawDto.reimburseWorkflowIds = this.reimbursementEditorGrid.instance.getSelectedRowKeys();
        }, 250);
    }

    onToolbarPreparing(event: any): void {
        event.toolbarOptions.items.unshift({
            location: this.getPosition(),
            widget: 'dxSelect',
            template: 'addReimbursementOptionsTemplate'
        });
    }

    onPlacementChange(event: any): void {
        this.toolbarButtonPosition = event;
    }

    onDownloadFile(record: WFReimbursementRequestLineItemsResultDto): void {
        const baseParams = new WorkFlowParametersDto();
        baseParams.workflowId = record.lineItemWorkflowId;
        baseParams.communityId = this.communityId;

        this.downloadInvoice(baseParams);
    }

    onInvoiceUploaderClose(forceReload: boolean): void {
        this.showAttachmentReceipt = false;

        if (Utils.isTrue(forceReload)) {
            this.getReimbursementLineItems();
        }
    }

    onReclassifyClose(forceReload: boolean): void {
        this.showReclassify = false;
        if (Utils.isTrue(forceReload)) {
            this.getReimbursementLineItems();
        }
    }

    private reclassifyWorkflow(): void {
        this.apBillDetailParam = new APBillParameterDto();
        this.apBillDetailParam.billId = this.selectedReimbursementLineItem.apBillId;
        this.apBillDetailParam.communityId = this.communityId;
        this.apBillDetailParam.managementCompanyId = this.managementCompanyId;

        this.showReclassify = true;
    }

    private getReimbursementLineItems(): void {
        const baseParam: WFReimbursementRequestEditorParameterDto = new WFReimbursementRequestEditorParameterDto();
        baseParam.communityId = this.communityId;
        if (Utils.notNullAndDefined(this.workflowId)) {
            baseParam.workflowId = this.workflowId;
        }

        this.loadingVisible = true;
        this.workflowReimbursementService.getWorkflowReimbursementEditorLineItems(baseParam).subscribe(
            (result: WFReimbursementRequestLineItemsResultDto[]) => {
                this.reimbursementEditor.lineItems = result;
                setTimeout(() => this.prepareLineItems(), 200);
                this.loadingVisible = false;
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private getReimbursementEditor(): void {
        const baseParam: WFReimbursementRequestEditorParameterDto = new WFReimbursementRequestEditorParameterDto();
        baseParam.communityId = this.communityId;

        if (Utils.notNullAndDefined(this.workflowId)) {
            baseParam.workflowId = this.workflowId;
        }

        this.loadingVisible = true;

        this.workflowReimbursementService.getWorkflowReimbursementEditor(baseParam).subscribe(
            (result: WFReimbursementRequestEditorResultDto) => {
                this.reimbursementEditor = result;
                this.reimbursementRawDto = Object.assign(this.reimbursementRawDto, result.reimbursementRawDto);
                this.prepareBankAccounts();
                this.prepareLineItems();

                this.loadingVisible = false;
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private prepareBankAccounts(): void {
        this.reimbursementEditor.sourceBankAccounts = this.prepareAccounts(this.reimbursementEditor.sourceBankAccounts);
        this.reimbursementEditor.targetBankAccounts = this.prepareAccounts(this.reimbursementEditor.targetBankAccounts);

        if (Utils.isNullOrUndefined(this.reimbursementRawDto.sourceBankAccountId)) {
            if (Utils.notNullOrEmpty(this.reimbursementEditor.sourceBankAccounts)) {
                const selectedSourceAccount = this.reimbursementEditor.sourceBankAccounts.find((d) =>
                    Utils.isTrue(d.isAPDefault)
                );
                if (Utils.notNullAndDefined(selectedSourceAccount)) {
                    this.sourceBankAccountBoxValue = [selectedSourceAccount.bankAccountId];
                }
            }
        } else {
            setTimeout(() => {
                this.sourceBankAccountBoxValue = [this.reimbursementRawDto.sourceBankAccountId];
            }, 500);
        }

        if (Utils.isNullOrUndefined(this.reimbursementRawDto.targetBankAccountId)) {
            if (Utils.notNullOrEmpty(this.reimbursementEditor.targetBankAccounts)) {
                const selectedTargetAccount = this.reimbursementEditor.targetBankAccounts.find((d) =>
                    Utils.isTrue(d.isAPDefault)
                );
                if (Utils.notNullOrEmpty(selectedTargetAccount)) {
                    this.sourceBankAccountBoxValue = [selectedTargetAccount.bankAccountId];
                }
            }
        } else {
            this.sourceBankAccountBoxValue = [this.reimbursementRawDto.targetBankAccountId];
        }
    }

    private prepareAccounts(accountSource: Array<WFEBankAccountResultDto>): Array<WFEBankAccountResultDto> {
        const bankAccountSource: Array<WFEBankAccountResultDto> = [];
        accountSource.forEach((account) => {
            let obj: any = {};
            obj = Object.assign(obj, account);
            obj.glDimensions = [];
            if (Utils.notNullOrEmpty(account.glDimensions)) {
                account.glDimensions.forEach((dim) => {
                    obj.glDimensions.push({
                        dimensionTitle: dim.dimensionTitle,
                        dimensionOptionTitle: dim.dimensionOptionTitle
                    });
                });
            }
            bankAccountSource.push(obj);
        });

        return bankAccountSource;
    }

    private prepareLineItems(): void {
        this.lineItemsSource = [];
        this.selectedReimbursementKeys = [];

        this.reimbursementEditor.lineItems.forEach((lineItem) => {
            lineItem.dimensions = lineItem.dimensions.replace(/,\s*$/, ''); // remove trailing commas

            if (
                this.reimbursementRawDto.reimburseWorkflowIds.find((d) => Utils.equals(d, lineItem.lineItemWorkflowId))
            ) {
                this.selectedReimbursementKeys.push(lineItem.lineItemWorkflowId);
            }

            if (Utils.isTrue(this.showReimbursableChargesOnly) && Utils.isTrue(lineItem.isReimbursable)) {
                this.lineItemsSource.push(lineItem);
            }

            if (Utils.isFalse(this.showReimbursableChargesOnly)) {
                this.lineItemsSource.push(lineItem);
            }
        });
    }

    private clearLineItemSelection(): void {
        const selectedRowKeys = this.reimbursementEditorGrid.instance.getSelectedRowKeys();
        if (Utils.notNullOrEmpty(selectedRowKeys)) {
            if (selectedRowKeys.find((d) => Utils.equals(d, this.selectedReimbursementLineItem.lineItemWorkflowId))) {
                this.reimbursementEditorGrid.instance.deselectRows([
                    this.selectedReimbursementLineItem.lineItemWorkflowId
                ]);
            }
        }
    }

    private toggleReimbursement(): void {
        const baseParam: WFToggleReimbursableParametersDto = new WFToggleReimbursableParametersDto();
        baseParam.communityId = this.communityId;
        baseParam.lineItemWorkflowId = this.selectedReimbursementLineItem.lineItemWorkflowId;

        this.workflowReimbursementService.toggleLineItemReimbursement(baseParam).subscribe(
            (result: any) => {
                this.getReimbursementLineItems();
                this.loadingVisible = false;
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private saveReimbursementEditor(): void {
        this.reimbursementRawDto.reimburseWorkflowIds = this.reimbursementEditorGrid.instance.getSelectedRowKeys();
        this.loadingVisible = true;
        this.workflowReimbursementService.saveWorkflowReimbursementEditor(this.reimbursementRawDto).subscribe(
            (result: any) => {
                notify('Reimbursement Request Updated Successfully!', 'Success', MessageInterval.successMsgInterval);
                this.closeEditor(true);
                this.loadingVisible = false;
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private getPosition(): string {
        let position = 'before';
        if (this.toolbarButtonPosition === 'bottom') {
            position = 'after';
        }
        return position;
    }

    private downloadInvoice(baseParams: WorkFlowParametersDto): void {
        this.loadingVisible = true;
        this.workflowSharedService.downloadInvoice(baseParams).subscribe(
            (result: Blob) => {
                const fileURL: string = this.helperService.getFileUrlFromBlob(result);
                window.open(fileURL, '_blank', 'location=yes, scrollbars=yes,status=yes');
                this.loadingVisible = false;
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private closeEditor(clearCache = false): void {
        this.onClose.emit(clearCache);
    }

    private getDefaultConfigurations(): void {
        this.lineitemFilterOptions = [
            {
                displayText: 'Show Reimbursable Debit Card Charges Only',
                value: true
            },
            {
                displayText: 'Show All Debit Charges only',
                value: false
            }
        ];
        this.showReimbursableChargesOnly = true;

        this.bankAccountColumns = [
            {
                dataField: 'accountName',
                dataType: 'string',
                caption: 'Bank Name'
            },
            {
                dataField: 'glAccountNumber',
                dataType: 'string',
                caption: 'GL Account Number'
            },
            {
                dataField: 'glAccountTitle',
                dataType: 'string',
                caption: 'GL Dimensions',
                cellTemplate: 'multiDimensionsTemplate'
            },
            {
                dataField: 'isAPDefault',
                dataType: 'string',
                caption: 'Is Default'
            }
        ];
    }
}
